import { useCallback, useEffect, useState } from 'react';

/**
 * 설정의 **상품 추천 On/Off** (v23, `docs/47` §9.6 · 절대 규칙 60).
 *
 * ★ **`ads-enabled.ts` 의 복제다.** 코드를 공유하지 않는 이유가 둘이다 — ① 저장소 키가
 * 다르고 ② **둘은 따로 켜고 끈다**(광고만 끈 채 상품을 보이는 것이 데모의 한 장면이다).
 * 하나로 묶으면 그 장면이 사라지고, 「제네릭 토글 훅」을 만들면 **이 화면에만 있는 규율**
 * (아래 ★ 셋)이 그 추상 뒤로 숨는다.
 *
 * ★★ **데모 장치이지 제품 기능이 아니다.** 켠 채와 끈 채를 나란히 보이기 위한 것이고,
 * 그래서 **서버에도 계정에도 저장하지 않는다** — `localStorage` 한 키다.
 * 실제 서비스에서 시청자는 이것을 끄지 못한다.
 *
 * **채널을 가리지 않는다.** 채널마다 두면 「이 채널만 상품이 뜬다」가 되고, 그것은
 * 정책(`offerPolicy`)이 이미 말하는 것이다. 이 토글이 말하는 것은 **지금 이 무대에서
 * 상품을 보일 것인가** 하나다.
 *
 * ★★★ **없으면 켜짐이다.** 정책이 진실이고 이 토글은 **억제**다 — 저장소가 막혀 있다고
 * (사파리 프라이빗) 상품이 통째로 사라지면, 데모 무대에서 그 사실을 아무도 모른 채
 * 「상품이 안 뜬다」를 서버에서 찾게 된다.
 */
const STORAGE_KEY = 'bloom.offersEnabled';

/** 같은 문서 안의 다른 컴포넌트에 알린다. `storage` 이벤트는 **다른 탭에만** 온다 */
const CHANGE_EVENT = 'bloom:offers-enabled';

export function offersEnabled(): boolean {
  try {
    // **`'false'` 만 끈 것이다.** 값이 없거나 이상하면 켜짐이다(위 ★★★).
    return localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function setOffersEnabled(next: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false');
  } catch {
    // 저장하지 못해도 이 탭에서는 아래 이벤트로 바뀐다. 새로고침하면 켜짐으로 돌아간다.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/**
 * 화면이 이 값을 그린다. **재생 경로는 이것을 구독하지 않는다** — 어댑터가
 * `requestOffer` 를 부르는 **그 순간** `offersEnabled()` 를 읽는다
 * (`create-offer-adapter.ts`).
 *
 * 그래서 토글이 **리시버를 다시 세우지 않는다** — 영상이 처음부터 다시 시작하지 않는
 * 것이 이 구조의 요점이다 (절대 규칙 25·60).
 */
export function useOffersEnabled(): [boolean, (next: boolean) => void] {
  const [enabled, setEnabled] = useState(offersEnabled);

  useEffect(() => {
    const sync = () => {
      setEnabled(offersEnabled());
    };
    window.addEventListener(CHANGE_EVENT, sync);
    // 다른 탭에서 바꾼 경우.
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const set = useCallback((next: boolean) => {
    setOffersEnabled(next);
    setEnabled(next);
  }, []);

  return [enabled, set];
}
